
export const getMaxAttemptsForLevel = (wordLength: number) => {
  const level = calculateLevelFromWordLength(wordLength);
  return 7 - level;
};

export const calculateLevelFromWordLength = (wordLength: number): number => {
  return wordLength - 2;
};

export const getWordLengthForLevel = (level: number): number => {
  return level + 2;
};

// Helper function to determine if a guess was "close"
export const isCloseGuess = (guess: { letter: string; status: string }[]): boolean => {
  // Normalize status names (misplaced/present -> wrong-position)
  const normalize = (status: string) => {
    if (status === "misplaced" || status === "present") return "wrong-position";
    return status;
  };
  
  const normalizedStatuses = guess.map(letter => normalize(letter.status));
  
  // Check if all letters are correct but misplaced (all yellow)
  const allWrongPosition = normalizedStatuses.every(status => status === "wrong-position");
  
  // Check if there's a mix of correct and wrong-position (no incorrect letters)
  const hasCorrect = normalizedStatuses.some(status => status === "correct");
  const hasWrongPosition = normalizedStatuses.some(status => status === "wrong-position");
  const hasIncorrect = normalizedStatuses.some(status => status === "incorrect");
  const notAllCorrect = !normalizedStatuses.every(status => status === "correct");
  
  // Close guess = (all yellow) OR (mix of green/yellow with no red letters)
  return allWrongPosition || (hasCorrect && hasWrongPosition && !hasIncorrect && notAllCorrect);
};

export const generateResultsVisual = (
  levelAttempts: number[] = [], 
  guessHistory: {
    level: number;
    word: string;
    guesses: { letter: string; status: string }[][];
  }[] = [],
  failedAtLevel?: number,
  includeLabels: boolean = true
) => {
  const results: string[] = [];
  
  // Ensure we have valid data
  if (!Array.isArray(levelAttempts) || !Array.isArray(guessHistory)) {
    console.warn('Invalid data provided to generateResultsVisual');
    return [];
  }
  
  console.log('Generating results visual with:', {
    levelAttempts,
    guessHistoryLength: guessHistory.length,
    failedAtLevel
  });
  
  for (let level = 1; level <= 4; level++) {
    const levelHistory = guessHistory?.find?.(h => h.level === level);
    const maxAttempts = getMaxAttemptsForLevel(getWordLengthForLevel(level));
    
    let resultLine = '';
    
    // Only add level label if includeLabels is true
    if (includeLabels) {
      resultLine += `Level ${level}: `;
    }
    
    if (levelHistory?.guesses?.length > 0) {
      const guessesCount = levelHistory.guesses.length;
      const lastGuess = levelHistory.guesses[guessesCount - 1];
      const wasSuccessful = lastGuess?.every(letter => letter.status === "correct");
      
      console.log(`Level ${level} analysis:`, {
        attempts: guessesCount,
        successful: wasSuccessful,
        isFailed: level === failedAtLevel
      });
      
      if (wasSuccessful && (!failedAtLevel || level < failedAtLevel)) {
        // Level completed successfully
        if (guessesCount === 1) {
          // First attempt success - only green, no red
          console.log(`Level ${level}: First attempt success!`);
          resultLine += '🟩' + '⬛'.repeat(Math.max(0, maxAttempts - 1));
        } else {
          // Multiple attempts needed - analyze each guess
          console.log(`Level ${level}: Success after ${guessesCount} attempts`);
          for (let i = 0; i < guessesCount - 1; i++) {
            const guess = levelHistory.guesses[i];
            resultLine += isCloseGuess(guess) ? '🟨' : '🟥';
          }
          resultLine += '🟩' + '⬛'.repeat(Math.max(0, maxAttempts - guessesCount));
        }
      } else if (level === failedAtLevel) {
        // Failed level - analyze each guess
        console.log(`Level ${level}: Failed after ${guessesCount} attempts, analyzing each guess`);
        for (let i = 0; i < guessesCount; i++) {
          const guess = levelHistory.guesses[i];
          resultLine += isCloseGuess(guess) ? '🟨' : '🟥';
        }
        resultLine += '⬛'.repeat(Math.max(0, maxAttempts - guessesCount));
      } else {
        // Level not completed successfully - analyze each guess
        console.log(`Level ${level}: Incomplete or failed`);
        for (let i = 0; i < guessesCount; i++) {
          const guess = levelHistory.guesses[i];
          resultLine += isCloseGuess(guess) ? '🟨' : '🟥';
        }
        resultLine += '⬛'.repeat(Math.max(0, maxAttempts - guessesCount));
      }
    } else {
      // Level not played - all black boxes
      resultLine += '⬛'.repeat(maxAttempts);
    }
    
    // Add a space at the end of each row and preserve it
    results.push(resultLine.trim() + ' ');
  }
  
  console.log('Final results visual:', results);
  return results;
};
